import React, { FormEvent, useState } from "react";
import { toLocalInput, toLocalDisplay } from "../../utils/datetimeLocal";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Settings,
  RefreshCw,
  Plus,
  Pencil,
  Trash2,
  Lock,
  Unlock,
  Activity,
  FileText,
  CheckCircle2,
  X,
  Info,
  BookOpen,
  Loader2,
  Layers,
  Calendar,
  History,
  ExternalLink,
} from "lucide-react";

import {
  useAdminGetExamQuery,
  useAdminUpdateExamMutation,
  useAdminGetExamAnalyticsQuery,
  useAdminGetDietsByExamQuery,
  useAdminCreateDietMutation,
  useAdminUpdateDietMutation,
  useAdminDeleteDietMutation,
} from "../../api/examsApi";
import {
  useAdminGetCoursesByExamQuery,
  useAdminCreateCourseMutation,
  useAdminUpdateCourseMutation as useAdminUpdateCourseMutationCourses,
  useAdminDeleteCourseMutation,
} from "../../api/adminExamCoursesApi";

import PageGate from "../../../../components/PageGate";
import AccessGate from "../../../../components/AccessGate";
import { PERMS } from "../../../../utils/perms";

function safeMsg(err: any) {
  return (
    err?.data?.message ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    "Something went wrong"
  );
}

function Modal({
  open,
  title,
  children,
  onClose,
}: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-900 rounded-xl text-white shadow-lg">
              <Settings className="w-4 h-4" />
            </div>
            <h3 className="font-black text-gray-900 text-xs uppercase tracking-widest">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  open: "bg-emerald-50 text-emerald-700 border-emerald-100",
  draft: "bg-gray-50 text-gray-400 border-gray-100",
  closed: "bg-rose-50 text-rose-700 border-rose-100",
  archived: "bg-amber-50 text-amber-700 border-amber-100",
};

export default function AdminExamDetailsPage() {
  const { id = "" } = useParams();

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: exam, isLoading, refetch } = useAdminGetExamQuery(id, { skip: !id });
  const {
    data: analytics,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useAdminGetExamAnalyticsQuery({ examId: id }, { skip: !id });

  // ── Exam mutation ──────────────────────────────────────────────────────────
  const [updateExam, { isLoading: savingExam }] = useAdminUpdateExamMutation();

  // ── Courses ────────────────────────────────────────────────────────────────
  const { data: examCourses = [], refetch: refetchCourses } =
    useAdminGetCoursesByExamQuery(id, { skip: !id });
  const [createCourse, { isLoading: creatingCourse }] = useAdminCreateCourseMutation();
  const [updateCourseField, { isLoading: updatingCourse }] = useAdminUpdateCourseMutationCourses();
  const [deleteCourse, { isLoading: deletingCourse }] = useAdminDeleteCourseMutation();

  const emptyCourseForm = { name: "", code: "", description: "", has_multiple_papers: false };
  const [courseForm, setCourseForm] = useState(emptyCourseForm);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [courseDeleteTarget, setCourseDeleteTarget] = useState<string | null>(null);

  // ── Diets ──────────────────────────────────────────────────────────────────
  const {
    data: examDiets = [],
    refetch: refetchDiets,
    isFetching: dietsFetching,
  } = useAdminGetDietsByExamQuery(id, { skip: !id });
  const [createDiet, { isLoading: creatingDiet }] = useAdminCreateDietMutation();
  const [updateDiet, { isLoading: updatingDiet }] = useAdminUpdateDietMutation();
  const [deleteDiet, { isLoading: deletingDiet }] = useAdminDeleteDietMutation();

  const emptyDietForm = {
    title: "",
    applications_open_at: "",
    applications_close_at: "",
    status: "draft" as "draft" | "open" | "closed",
    is_active: true,
  };
  const [dietForm, setDietForm] = useState(emptyDietForm);
  const [showDietForm, setShowDietForm] = useState(false);
  const [dietEditTarget, setDietEditTarget] = useState<any | null>(null);
  const [dietDeleteTarget, setDietDeleteTarget] = useState<string | null>(null);
  const [dietBusyId, setDietBusyId] = useState<string | null>(null);

  // ── UI State ───────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // ── Exam edit ──────────────────────────────────────────────────────────────
  const [editExamOpen, setEditExamOpen] = useState(false);
  const [editExamForm, setEditExamForm] = useState({
    code: "",
    title: "",
    requires_logbook_stage: "none",
    is_active: true,
  });

  // ── Handlers ───────────────────────────────────────────────────────────────
  const showToast = (type: "success" | "error", text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const openExamEdit = () => {
    setEditExamForm({
      code: exam?.code ?? "",
      title: exam?.title ?? "",
      requires_logbook_stage: exam?.requires_logbook_stage ?? "none",
      is_active: !!exam?.is_active,
    });
    setEditExamOpen(true);
  };

  const onSaveExam = async () => {
    try {
      await updateExam({ id, body: editExamForm } as any).unwrap();
      showToast("success", "Exam updated.");
      setEditExamOpen(false);
      refetch();
    } catch (e) {
      showToast("error", safeMsg(e));
    }
  };

  const onToggleExamActive = async () => {
    const next = !exam?.is_active;
    setBusyId(id);
    try {
      await updateExam({ id, body: { is_active: next } } as any).unwrap();
      showToast("success", `Exam ${next ? "activated" : "deactivated"}`);
      refetch();
    } catch (e) {
      showToast("error", safeMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const onAddCourse = async () => {
    if (!courseForm.name.trim() || !courseForm.code.trim()) {
      showToast("error", "Course name and code are required.");
      return;
    }
    try {
      await createCourse({
        exam_id: id,
        name: courseForm.name,
        code: courseForm.code,
        description: courseForm.description || undefined,
        has_multiple_papers: courseForm.has_multiple_papers,
      }).unwrap();
      setCourseForm(emptyCourseForm);
      setShowCourseForm(false);
      showToast("success", "Course added.");
      refetchCourses();
    } catch (e) {
      showToast("error", safeMsg(e));
    }
  };

  const onToggleCourseMultiPaper = async (c: any) => {
    try {
      await updateCourseField({ id: c.id, body: { has_multiple_papers: !c.has_multiple_papers } }).unwrap();
      refetchCourses();
    } catch (e) {
      showToast("error", safeMsg(e));
    }
  };

  const onToggleCourseActive = async (c: any) => {
    try {
      await updateCourseField({ id: c.id, body: { is_active: !c.is_active } }).unwrap();
      refetchCourses();
    } catch (e) {
      showToast("error", safeMsg(e));
    }
  };

  const onDeleteCourse = async (courseId: string) => {
    try {
      await deleteCourse(courseId).unwrap();
      setCourseDeleteTarget(null);
      showToast("success", "Course removed.");
      refetchCourses();
    } catch (e) {
      showToast("error", safeMsg(e));
    }
  };

  // Diet handlers
  const onAddDiet = async (e: FormEvent) => {
    e.preventDefault();
    if (!dietForm.title.trim()) {
      showToast("error", "Diet title is required.");
      return;
    }
    if (!dietForm.applications_open_at || !dietForm.applications_close_at) {
      showToast("error", "Opening and closing dates are required.");
      return;
    }
    if (dietForm.applications_close_at <= dietForm.applications_open_at) {
      showToast("error", "Closing date must be after opening date.");
      return;
    }
    try {
      await createDiet({ ...dietForm, exam_id: id }).unwrap();
      setDietForm(emptyDietForm);
      setShowDietForm(false);
      showToast("success", "Diet created.");
      refetchDiets();
    } catch (e) {
      showToast("error", safeMsg(e));
    }
  };

  const onToggleDietStatus = async (d: any) => {
    setDietBusyId(d.id);
    try {
      await updateDiet({
        id: d.id,
        body: { status: d.status === "open" ? "closed" : "open" },
      }).unwrap();
      showToast("success", "Diet status updated.");
      refetchDiets();
    } catch (e) {
      showToast("error", safeMsg(e));
    } finally {
      setDietBusyId(null);
    }
  };

  const onSaveDiet = async () => {
    if (!dietEditTarget) return;
    setDietBusyId(dietEditTarget.id);
    try {
      await updateDiet({ id: dietEditTarget.id, body: dietEditTarget }).unwrap();
      setDietEditTarget(null);
      showToast("success", "Diet updated.");
      refetchDiets();
    } catch (e) {
      showToast("error", safeMsg(e));
    } finally {
      setDietBusyId(null);
    }
  };

  const onDeleteDiet = async (dietId: string) => {
    setDietBusyId(dietId);
    try {
      await deleteDiet(dietId).unwrap();
      setDietDeleteTarget(null);
      showToast("success", "Diet deleted.");
      refetchDiets();
    } catch (e) {
      showToast("error", safeMsg(e));
    } finally {
      setDietBusyId(null);
    }
  };

  return (
    <PageGate perm={PERMS.exam.access}>
      <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">

        {/* Section 1: Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-4">
            <Link
              to="/exams/admin/exams"
              className="flex items-center gap-2 text-gray-400 hover:text-gray-900 transition-colors text-[10px] font-black uppercase tracking-widest"
            >
              <ArrowLeft className="w-3 h-3" /> Back to Registry
            </Link>
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-[2rem] bg-gray-900 flex items-center justify-center text-white shadow-2xl">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase leading-none">
                    {exam?.title || "Exam Spec"}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                      exam?.is_active
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : "bg-gray-50 text-gray-400 border-gray-100"
                    }`}
                  >
                    {exam?.is_active ? "Live" : "Inactive"}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mt-2 font-bold uppercase tracking-widest">
                  {exam?.code} •{" "}
                  {exam?.requires_logbook_stage !== "none"
                    ? `Requires ${exam?.requires_logbook_stage}`
                    : "No Logbook"}{" "}
                  • {(examCourses as any[]).length} Course{(examCourses as any[]).length !== 1 ? "s" : ""}
                  {" "}• {(examDiets as any[]).length} Diet{(examDiets as any[]).length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <AccessGate perm={PERMS.exam.edit}>
              <button
                onClick={openExamEdit}
                className="px-6 py-3 rounded-2xl border-2 border-gray-100 hover:border-gray-900 hover:bg-gray-900 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all"
              >
                Edit Specs
              </button>
              <button
                disabled={busyId === id || savingExam}
                onClick={onToggleExamActive}
                className="p-3.5 rounded-2xl border-2 border-gray-100 hover:bg-gray-50 transition-all"
              >
                {exam?.is_active ? (
                  <Lock className="w-4 h-4 text-amber-500" />
                ) : (
                  <Unlock className="w-4 h-4 text-emerald-500" />
                )}
              </button>
            </AccessGate>
            <button
              onClick={() => { refetch(); refetchAnalytics(); refetchDiets(); }}
              className="p-3.5 rounded-2xl border-2 border-gray-100 hover:bg-gray-50 transition-all"
            >
              <RefreshCw
                className={`w-4 h-4 text-indigo-500 ${
                  (isLoading || analyticsLoading || dietsFetching) && "animate-spin"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div
            className={`p-4 rounded-2xl border flex items-center justify-between animate-in slide-in-from-top-2 ${
              toast.type === "success"
                ? "bg-gray-900 border-gray-800 text-white"
                : "bg-rose-50 border-rose-100 text-rose-900"
            }`}
          >
            <span className="text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
              {toast.type === "success" ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              ) : (
                <Info className="w-4 h-4" />
              )}
              {toast.text}
            </span>
            <button onClick={() => setToast(null)}>
              <X className="w-4 h-4 opacity-50 hover:opacity-100" />
            </button>
          </div>
        )}

        {/* Section 2: Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white border border-gray-100 p-8 rounded-[2.5rem] shadow-sm">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
              Total Applications
            </div>
            <div className="text-5xl font-black text-gray-900 tracking-tighter">
              {analytics?.totals?.applications ?? 0}
            </div>
          </div>
          <div className="bg-white border border-gray-100 p-8 rounded-[2.5rem] shadow-sm md:col-span-3">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">
              Application Status Breakdown
            </div>
            {Object.keys(analytics?.by_status ?? {}).length === 0 ? (
              <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">
                No applications yet
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(analytics?.by_status ?? {}).map(([k, v]: any) => (
                  <div
                    key={k}
                    className="bg-gray-50 px-4 py-2 rounded-xl border border-gray-100 text-[10px] font-black uppercase tracking-tighter"
                  >
                    <span className="text-gray-400 mr-2">{k}:</span> {v}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Paid", val: analytics?.by_status?.paid ?? 0, color: "emerald" },
              { label: "Approved", val: analytics?.by_status?.approved ?? 0, color: "indigo" },
              { label: "Rejected", val: analytics?.by_status?.rejected ?? 0, color: "rose" },
              { label: "Completed", val: analytics?.by_status?.completed ?? 0, color: "amber" },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-white border border-gray-100 rounded-[2rem] p-6 shadow-sm flex items-center gap-4"
              >
                <div className={`w-10 h-10 rounded-2xl bg-${s.color}-50 flex items-center justify-center shrink-0`}>
                  <Activity className={`w-5 h-5 text-${s.color}-500`} />
                </div>
                <div>
                  <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{s.label}</div>
                  <div className="text-2xl font-black text-gray-900 tracking-tighter">{s.val}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Exam Diets */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
          <div className="p-8 border-b bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <History className="w-5 h-5 text-indigo-600" />
              <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">
                Exam Diets ({(examDiets as any[]).length})
              </h2>
            </div>
            <AccessGate perm={PERMS.exam.dietCreate}>
              <button
                onClick={() => setShowDietForm((p) => !p)}
                className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 transition-all active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" />
                {showDietForm ? "Cancel" : "Add Diet"}
              </button>
            </AccessGate>
          </div>

          {/* Inline Add Diet Form */}
          {showDietForm && (
            <form onSubmit={onAddDiet} className="px-8 py-6 border-b border-gray-50 bg-indigo-50/30">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                    Diet Title *
                  </label>
                  <input
                    value={dietForm.title}
                    onChange={(e) => setDietForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="e.g. Diet A 2026"
                    className="w-full border-b-2 border-gray-200 py-2 font-bold text-gray-800 text-sm outline-none focus:border-indigo-500 bg-transparent"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                    Opens At *
                  </label>
                  <input
                    type="datetime-local"
                    value={dietForm.applications_open_at}
                    onChange={(e) => setDietForm((p) => ({ ...p, applications_open_at: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                    Closes At *
                  </label>
                  <input
                    type="datetime-local"
                    value={dietForm.applications_close_at}
                    onChange={(e) => setDietForm((p) => ({ ...p, applications_close_at: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                    Initial Status:
                  </label>
                  <select
                    value={dietForm.status}
                    onChange={(e) => setDietForm((p) => ({ ...p, status: e.target.value as any }))}
                    className="border-b-2 border-gray-200 py-1 px-1 text-[10px] font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent uppercase"
                  >
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={creatingDiet}
                  className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-6 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95"
                >
                  {creatingDiet ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  {creatingDiet ? "Creating…" : "Create Diet"}
                </button>
              </div>
            </form>
          )}

          {/* Diet list */}
          {(examDiets as any[]).length === 0 ? (
            <div className="py-20 text-center">
              <Calendar className="w-10 h-10 mx-auto text-gray-100 mb-3" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
                No diets configured for this exam yet
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {(examDiets as any[]).map((d: any) => {
                const isBusy = dietBusyId === d.id;
                return (
                  <div
                    key={d.id}
                    className="group flex items-center justify-between px-8 py-5 hover:bg-gray-50/80 transition-all gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-black text-gray-900 uppercase tracking-tight">{d.title}</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                            STATUS_COLORS[d.status] ?? "bg-gray-50 text-gray-400 border-gray-100"
                          }`}
                        >
                          {d.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        {d.applications_open_at && (
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                            Open: {toLocalDisplay(d.applications_open_at)}
                          </span>
                        )}
                        {d.applications_close_at && (
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                            Close: {toLocalDisplay(d.applications_close_at)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Link
                        to={`/exams/admin/exams/${id}/diets/${d.id}`}
                        title="View diet details"
                        className="p-2.5 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <AccessGate perm={PERMS.exam.dietEdit}>
                        <button
                          onClick={() => setDietEditTarget({ ...d })}
                          title="Edit diet"
                          className="p-2.5 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          disabled={isBusy}
                          onClick={() => onToggleDietStatus(d)}
                          title={d.status === "open" ? "Close enrollment" : "Open enrollment"}
                          className="p-2.5 text-gray-300 hover:text-amber-500 hover:bg-amber-50 rounded-xl transition-all"
                        >
                          {d.status === "open" ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <Unlock className="w-4 h-4" />
                          )}
                        </button>
                      </AccessGate>

                      <AccessGate perm={PERMS.exam.dietDelete}>
                        {dietDeleteTarget === String(d.id) ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => onDeleteDiet(String(d.id))}
                              disabled={isBusy || deletingDiet}
                              className="px-3 py-1.5 bg-rose-600 text-white rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-rose-700 transition-all disabled:opacity-30"
                            >
                              {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirm"}
                            </button>
                            <button
                              onClick={() => setDietDeleteTarget(null)}
                              className="px-2 py-1.5 text-[9px] font-black uppercase text-gray-400 hover:text-gray-600"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDietDeleteTarget(String(d.id))}
                            className="p-2.5 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </AccessGate>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Section 4: Courses */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
          <div className="p-8 border-b bg-gray-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              <h2 className="text-[10px] font-black uppercase tracking-widest text-gray-900">
                Courses ({(examCourses as any[]).length})
              </h2>
            </div>
            <AccessGate perm={PERMS.exam.create}>
              <button
                onClick={() => setShowCourseForm((p) => !p)}
                className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-5 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 transition-all active:scale-95"
              >
                <Plus className="w-3.5 h-3.5" />
                {showCourseForm ? "Cancel" : "Add Course"}
              </button>
            </AccessGate>
          </div>

          {/* Inline Add Course Form */}
          {showCourseForm && (
            <div className="px-8 py-6 border-b border-gray-50 bg-indigo-50/30">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Course Name *</label>
                  <input
                    value={courseForm.name}
                    onChange={(e) => setCourseForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Construction Technology"
                    className="w-full border-b-2 border-gray-200 py-2 font-bold text-gray-800 text-sm outline-none focus:border-indigo-500 bg-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Code *</label>
                  <input
                    value={courseForm.code}
                    onChange={(e) => setCourseForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. QS101"
                    className="w-full border-b-2 border-gray-200 py-2 font-bold text-gray-800 text-sm outline-none focus:border-indigo-500 bg-transparent uppercase"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Description</label>
                  <input
                    value={courseForm.description}
                    onChange={(e) => setCourseForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Optional"
                    className="w-full border-b-2 border-gray-200 py-2 font-bold text-gray-800 text-sm outline-none focus:border-indigo-500 bg-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <div
                    onClick={() => setCourseForm((p) => ({ ...p, has_multiple_papers: !p.has_multiple_papers }))}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${
                      courseForm.has_multiple_papers ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        courseForm.has_multiple_papers ? "translate-x-5" : ""
                      }`}
                    />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-600">Multi-Paper Course</span>
                </label>
                <button
                  onClick={onAddCourse}
                  disabled={creatingCourse}
                  className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-6 py-3 text-[9px] font-black uppercase tracking-[0.15em] hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95"
                >
                  {creatingCourse ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  {creatingCourse ? "Saving…" : "Add Course"}
                </button>
              </div>
            </div>
          )}

          {/* Course list */}
          {(examCourses as any[]).length === 0 ? (
            <div className="py-20 text-center">
              <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No courses added to this exam yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {(examCourses as any[]).map((c: any) => (
                <div key={c.id} className="group flex items-center justify-between px-8 py-5 hover:bg-gray-50/80 transition-all gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-black text-[10px] text-indigo-600 uppercase tracking-widest">{c.code}</span>
                      <span className="font-black text-gray-900 uppercase tracking-tight">{c.name}</span>
                      {c.has_multiple_papers && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-100">
                          Multi-Paper
                        </span>
                      )}
                      {!c.is_active && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-gray-50 text-gray-400 border border-gray-100">
                          Inactive
                        </span>
                      )}
                    </div>
                    {c.description && (
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 truncate max-w-md">
                        {c.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <AccessGate perm={PERMS.exam.edit}>
                      <button
                        onClick={() => onToggleCourseMultiPaper(c)}
                        disabled={updatingCourse}
                        title={c.has_multiple_papers ? "Switch to single-paper" : "Switch to multi-paper"}
                        className="p-2.5 text-gray-300 hover:text-purple-500 hover:bg-purple-50 rounded-xl transition-all"
                      >
                        <Layers className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onToggleCourseActive(c)}
                        disabled={updatingCourse}
                        title={c.is_active ? "Deactivate" : "Activate"}
                        className="p-2.5 text-gray-300 hover:text-amber-500 hover:bg-amber-50 rounded-xl transition-all"
                      >
                        {c.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                      </button>
                    </AccessGate>
                    <AccessGate perm={PERMS.exam.delete}>
                      {courseDeleteTarget === String(c.id) ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onDeleteCourse(String(c.id))}
                            disabled={deletingCourse}
                            className="px-3 py-1.5 bg-rose-600 text-white rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-rose-700 transition-all disabled:opacity-30"
                          >
                            {deletingCourse ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirm"}
                          </button>
                          <button
                            onClick={() => setCourseDeleteTarget(null)}
                            className="px-2 py-1.5 text-[9px] font-black uppercase text-gray-400 hover:text-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setCourseDeleteTarget(String(c.id))}
                          className="p-2.5 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </AccessGate>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit Exam Modal */}
        <Modal
          open={editExamOpen}
          title="Reconfigure Exam"
          onClose={() => setEditExamOpen(false)}
        >
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <input
                value={editExamForm.code}
                onChange={(e) => setEditExamForm({ ...editExamForm, code: e.target.value })}
                className="border-b-2 border-gray-100 py-2 font-black uppercase"
                placeholder="Code"
              />
              <select
                value={editExamForm.requires_logbook_stage}
                onChange={(e) =>
                  setEditExamForm({ ...editExamForm, requires_logbook_stage: e.target.value })
                }
                className="border-b-2 border-gray-100 py-2 font-black text-xs"
              >
                <option value="none">No Logbook</option>
                <option value="stage1">Stage 1</option>
                <option value="stage2">Stage 2</option>
              </select>
            </div>
            <input
              value={editExamForm.title}
              onChange={(e) => setEditExamForm({ ...editExamForm, title: e.target.value })}
              className="w-full border-b-2 border-gray-100 py-2 font-black"
              placeholder="Exam Title"
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editExamForm.is_active}
                onChange={(e) => setEditExamForm({ ...editExamForm, is_active: e.target.checked })}
              />{" "}
              <span className="text-[10px] font-black uppercase">Active</span>
            </label>
            <button
              onClick={onSaveExam}
              className="w-full bg-gray-900 text-white rounded-[1.5rem] py-5 text-[10px] font-black uppercase tracking-widest shadow-xl"
            >
              Commit Changes
            </button>
          </div>
        </Modal>

        {/* Edit Diet Modal */}
        {dietEditTarget && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
              onClick={() => setDietEditTarget(null)}
            />
            <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gray-900 rounded-2xl text-white shadow-lg">
                    <Pencil className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-black text-gray-900 text-sm uppercase tracking-tighter leading-none">
                      Edit Diet
                    </h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5">
                      Modify cycle settings
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDietEditTarget(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-8 space-y-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Title
                  </label>
                  <input
                    value={dietEditTarget.title}
                    onChange={(e) => setDietEditTarget({ ...dietEditTarget, title: e.target.value })}
                    className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Status
                  </label>
                  <select
                    value={dietEditTarget.status}
                    onChange={(e) => setDietEditTarget({ ...dietEditTarget, status: e.target.value })}
                    className="w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Opens At
                    </label>
                    <input
                      type="datetime-local"
                      value={toLocalInput(dietEditTarget.applications_open_at)}
                      onChange={(e) =>
                        setDietEditTarget({ ...dietEditTarget, applications_open_at: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Closes At
                    </label>
                    <input
                      type="datetime-local"
                      value={toLocalInput(dietEditTarget.applications_close_at)}
                      onChange={(e) =>
                        setDietEditTarget({ ...dietEditTarget, applications_close_at: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="p-8 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setDietEditTarget(null)}
                  className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={onSaveDiet}
                  disabled={updatingDiet || dietBusyId === dietEditTarget.id}
                  className="flex-[2] bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-indigo-600 transition-all disabled:opacity-30"
                >
                  {updatingDiet ? "Saving…" : "Apply Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
